import { format, type BigNumber, type FormatStyle } from "@yjsgames/bignumber";
import { getLanguage, t } from "../i18n/i18n.js";
import type { NumberFormat } from "../models/ModuleBrickSaveData.js";

/**
 * 앱 전역 큰 숫자 표기 — 현재 표기 형식(설정) + 현재 언어(i18n)를 모듈 상태로 들고,
 * formatBig(value)로 모든 View가 동일 형식·언어 문자열을 얻는다 (i18n setLanguage/t와 대칭 구조).
 * 형식 변경은 setNumberFormat() 후 GameScene.refreshAll()이 각 View update()를 재호출.
 */
let currentFormat: NumberFormat = "auto";

/** 앱 표기 설정값(auto/kmg/eastAsian/alphabet) → 패키지 FormatStyle. */
const STYLE_MAP: Record<NumberFormat, FormatStyle> = {
  auto: "locale",
  kmg: "kmg",
  eastAsian: "myriad",
  alphabet: "alphabet",
};

export function setNumberFormat(f: NumberFormat): void {
  currentFormat = f;
}

export function getNumberFormat(): NumberFormat {
  return currentFormat;
}

/** BigNumber → 현재 형식·언어 표시 문자열. 언어는 i18n getLanguage()를 SSoT로 재사용. */
export function formatBig(value: BigNumber): string {
  return format(value, { style: STYLE_MAP[currentFormat], locale: getLanguage() });
}

/**
 * 쿨타임 카운트다운 시계 표기 — ms → "H:MM:SS"(1시간+) 또는 "M:SS"(1시간 미만). 언어 무관(콜론 형식).
 * 상점 광고 다이아 등 1초 틱 카운트다운 라벨 공용 (다른 쿨타임에 재사용).
 */
export function formatClock(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/**
 * 칸 해금 비용 표시 라벨 SSoT — 그리드 잠금셀(ModuleGridView)과 확인 모달(GridUnlockConfirmPopup)이
 * 동일 다이아 표기를 쓰도록 통일한다. 과거 그리드는 i18n 래핑("50G"), 모달은 raw formatBig("50")으로
 * 어긋나 "가격이 다르다"는 버그가 있었다(unlockCostLabel.test.ts 회귀 게이트). 숫자 포맷은 formatBig 경유.
 */
export function unlockCostLabel(costBig: BigNumber): string {
  return t("grid.unlockCost", { cost: formatBig(costBig) });
}
